import React, { useState, useMemo } from 'react';
import { M, Flex, Multiselect } from 'design-system-web';
import { Trans, useTranslation } from 'react-i18next';
import { ConfirmationModal } from 'components/general/ConfirmationModal';
import { Label } from 'components';
import { removeCompanyRole, getAvailableCompanyRoles } from 'graphql-queries';
import { useMutation, useQuery } from '@apollo/client';
import { AvailableCompanyRole, RoleOption } from './types';

type Props = {
  isOpen: boolean;
  bulkSelectedUsers: {
    id: string;
    name: string;
    email: string;
  }[];
  onClose: () => void;
  onSuccess?: () => void;
  propertyId: string;
};

const TRANSLATION_KEYS = {
  modalTitle:
    'web.admin.channel.users.all.table.bulkRemoveCompanyRole.modal.title',
  removeButtonLabel:
    'web.admin.channel.users.all.table.bulkRemoveCompanyRole.modal.removeButton.label',
  cancelButtonLabel:
    'web.admin.channel.users.all.table.bulkRemoveCompanyRole.modal.cancelButton.label',
  promptText:
    'web.admin.channel.users.all.table.bulkRemoveCompanyRole.modal.prompt.text',
  roleDropdownLabel:
    'web.admin.channel.users.all.table.bulkRemoveCompanyRole.modal.roleDropdown.label',
  toastText:
    'web.admin.channel.users.all.table.bulkRemoveCompanyRole.success.toast.text',
  errorMessage:
    'web.admin.channel.users.all.table.bulkRemoveCompanyRole.failed.alert.text',
};

export const BulkRemoveCompanyRoleModal = ({
  isOpen,
  bulkSelectedUsers,
  onClose,
  onSuccess,
  propertyId,
}: Props) => {
  const [selectedRoles, setSelectedRoles] = useState<{ value: string }[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation();

  const [removeCompanyRoleMutation] = useMutation(removeCompanyRole);

  const {
    data: companyRolesData,
    loading: rolesLoading,
    error: rolesError,
  } = useQuery(getAvailableCompanyRoles, {
    variables: {
      propertyId,
    },
    skip: !isOpen,
    fetchPolicy: 'cache-and-network',
  });

  // Handle query errors
  React.useEffect(() => {
    if (rolesError) {
      window.Alert.alert({
        title: t(TRANSLATION_KEYS.errorMessage),
        error: rolesError,
      });
    }
  }, [rolesError, t]);

  const roleOptions: RoleOption[] = useMemo(() => {
    const availableRoles = companyRolesData?.getAvailableCompanyRoles ?? [];

    return availableRoles.map((role: AvailableCompanyRole) => ({
      value: role.templateId,
      label: role.name,
    }));
  }, [companyRolesData]);

  const handleRolesChange = (roles: { value: string }[]) => {
    setSelectedRoles(roles);
  };

  const closeModal = () => {
    onClose();
    setSelectedRoles([]);
  };

  const handleConfirm = async () => {
    if (selectedRoles.length === 0) return;

    try {
      setLoading(true);

      const selectedRoleIds = selectedRoles.map(({ value }) => value);

      await removeCompanyRoleMutation({
        variables: {
          userIds: bulkSelectedUsers.map(user => user.id),
          roleTemplateIds: selectedRoleIds,
          channelId: propertyId,
        },
      });

      // Show success message
      window.Toast.show(t(TRANSLATION_KEYS.toastText), 5000);

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      await window.Alert.alert({
        title: t(TRANSLATION_KEYS.errorMessage),
        error: err,
      });
    }

    setLoading(false);
    closeModal();
  };

  const handleClose = () => {
    closeModal();
  };

  const bulkSelectedUsersCount = bulkSelectedUsers.length;
  const selectedRolesCount = selectedRoles.length;

  return (
    <ConfirmationModal
      isOpen={isOpen}
      onClose={handleClose}
      handleConfirm={handleConfirm}
      title={t(TRANSLATION_KEYS.modalTitle)}
      confirmText={t(TRANSLATION_KEYS.removeButtonLabel, {
        count: selectedRolesCount,
      })}
      cancelText={t(TRANSLATION_KEYS.cancelButtonLabel)}
      loading={loading}
      confirmActionDisabled={selectedRolesCount === 0 || loading}
      cancelActionDisabled={loading}
      isFullWidth
    >
      <M>
        <Trans
          components={[<b />]}
          i18nKey={TRANSLATION_KEYS.promptText}
          count={bulkSelectedUsersCount}
          values={{
            count: bulkSelectedUsersCount,
          }}
        />
      </M>
      <Flex direction="column" gap={1} m={[4, 0, 0, 0]}>
        <Label>{t(TRANSLATION_KEYS.roleDropdownLabel)}</Label>
        <Multiselect
          id="role-select"
          testId="company-role-dropdown"
          items={roleOptions}
          isLoading={rolesLoading}
          disabled={loading || rolesLoading}
          value={selectedRoles}
          onChange={handleRolesChange}
          placeholder={t(
            'web.admin.channel.users.all.table.bulkRemoveCompanyRole.modal.roleDropdown.placeholder'
          )}
          isFullWidth
          doTranslation={false}
        />
      </Flex>
    </ConfirmationModal>
  );
};

